import {
  getGetApiUtilitiesOverviewQueryOptions,
  useGetApiUtilitiesConfig,
  type UtilitiesCategoryOverview,
} from '@smarthome/api';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Check, ChevronDown, Zap } from 'lucide-react';
import { memo, useMemo, useState } from 'react';
import { z } from 'zod';

import type { ConfigFormProps, RendererProps, WidgetDefinition } from '../types';
import { useDashboardTimeRange } from '../viewer/DashboardTimeRangeContext';

/**
 * Time preset options for utility widgets
 */
type UtilityPreset =
  | 'today'
  | 'yesterday'
  | '7d'
  | '30d'
  | 'mtd'
  | 'ytd'
  | 'lastWeek'
  | 'lastMonth';

const PRESET_OPTIONS: { value: UtilityPreset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'mtd', label: 'Month to date' },
  { value: 'ytd', label: 'Year to date' },
  { value: 'lastWeek', label: 'Last week' },
  { value: 'lastMonth', label: 'Last month' },
];

/**
 * Map dashboard time range to nearest utility preset
 */
function dashboardRangeToPreset(range: string): UtilityPreset {
  if (range === '7d') return '7d';
  if (range === '30d') return '30d';
  // '1h', '6h', '24h' → today is the closest calendar concept
  return 'today';
}

type ValueMode = 'cost' | 'consumption' | 'both';

/**
 * Utilities stat widget config schema
 */
export const utilitiesStatConfigSchema = z.object({
  categoryIds: z.array(z.string()).min(1, 'At least one category is required'),
  valueMode: z.enum(['cost', 'consumption', 'both']).default('both'),
  timeRangeMode: z.enum(['dashboard', 'fixed']).default('fixed'),
  preset: z
    .enum(['today', 'yesterday', '7d', '30d', 'mtd', 'ytd', 'lastWeek', 'lastMonth'])
    .default('today'),
});

export type UtilitiesStatConfig = z.infer<typeof utilitiesStatConfigSchema>;

/**
 * Category multi-select component
 */
function CategoryMultiSelect({
  selectedIds,
  onChange,
  error,
}: {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  error?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { data: configData, isLoading } = useGetApiUtilitiesConfig();
  const categories = configData?.config?.categories ?? [];

  const handleToggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((i) => i !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const selectedNames = categories
    .filter((c) => c.id && selectedIds.includes(c.id))
    .map((c) => c.name ?? c.id ?? '')
    .filter(Boolean);

  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
        Categories
      </label>

      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${
            error
              ? 'border-red-300 bg-red-50/50 dark:border-red-500/30 dark:bg-red-500/10'
              : 'border-gray-200 bg-white hover:border-gray-300 dark:border-white/[0.08] dark:bg-zinc-900 dark:hover:border-white/[0.12]'
          }`}
        >
          <span className="truncate text-gray-500 dark:text-zinc-500">
            {selectedNames.length > 0 ? selectedNames.join(', ') : 'Select categories...'}
          </span>
          <ChevronDown
            className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {isOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
            <div className="absolute left-0 right-0 z-20 mt-1 max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-white/[0.08] dark:bg-zinc-900">
              {isLoading ? (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-zinc-500">
                  Loading categories...
                </div>
              ) : categories.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-zinc-500">
                  No utility categories configured
                </div>
              ) : (
                categories.map((category) => {
                  const id = category.id ?? '';
                  const isSelected = selectedIds.includes(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => handleToggle(id)}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-white/[0.04]"
                    >
                      <span className="text-gray-900 dark:text-white">{category.name ?? id}</span>
                      {isSelected && <Check className="h-4 w-4 text-blue-500" />}
                    </button>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>

      {error && (
        <p className="mt-1.5 flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
          <AlertTriangle className="h-3.5 w-3.5" />
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Config form for Utilities Stat widget
 */
function UtilitiesStatConfigForm({
  config,
  onChange,
  errors,
}: ConfigFormProps<UtilitiesStatConfig>) {
  return (
    <div className="space-y-4">
      <CategoryMultiSelect
        selectedIds={config.categoryIds}
        onChange={(ids) => onChange({ ...config, categoryIds: ids })}
        error={errors?.['categoryIds']}
      />

      <div>
        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
          Show Values
        </label>
        <select
          value={config.valueMode}
          onChange={(e) => onChange({ ...config, valueMode: e.target.value as ValueMode })}
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 dark:border-white/[0.08] dark:bg-zinc-900 dark:text-white"
        >
          <option value="both">Cost &amp; consumption</option>
          <option value="cost">Cost only</option>
          <option value="consumption">Consumption only</option>
        </select>
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
          Time Range
        </label>
        <select
          value={config.timeRangeMode}
          onChange={(e) =>
            onChange({ ...config, timeRangeMode: e.target.value as 'dashboard' | 'fixed' })
          }
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 dark:border-white/[0.08] dark:bg-zinc-900 dark:text-white"
        >
          <option value="dashboard">Use dashboard timeframe</option>
          <option value="fixed">Fixed timeframe</option>
        </select>
      </div>

      {config.timeRangeMode === 'fixed' && (
        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
            Period
          </label>
          <select
            value={config.preset}
            onChange={(e) => onChange({ ...config, preset: e.target.value as UtilityPreset })}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 dark:border-white/[0.08] dark:bg-zinc-900 dark:text-white"
          >
            {PRESET_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

/**
 * Format a monetary value with currency code
 */
function formatCost(value: number, currencyCode: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currencyCode}`;
  }
}

/**
 * Format a consumption value with unit symbol
 */
function formatConsumption(value: number, unitSymbol: string): string {
  const formatted =
    value >= 1000
      ? value.toLocaleString(undefined, { maximumFractionDigits: 1 })
      : value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  return unitSymbol ? `${formatted} ${unitSymbol}` : formatted;
}

/**
 * Single category stat card
 */
function CategoryStatCard({
  category,
  valueMode,
  currencyCode,
  compact,
}: {
  category: UtilitiesCategoryOverview;
  valueMode: ValueMode;
  currencyCode: string;
  compact: boolean;
}) {
  const hasCost = valueMode === 'cost' || valueMode === 'both';
  const hasConsumption = valueMode === 'consumption' || valueMode === 'both';
  const showBoth = valueMode === 'both';

  const costStr = hasCost ? formatCost(category.cost ?? 0, currencyCode) : null;
  const consumptionStr = hasConsumption
    ? formatConsumption(category.consumption ?? 0, category.unitSymbol ?? '')
    : null;

  if (compact) {
    // Multi-category compact card
    return (
      <div className="flex flex-col gap-0.5 rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-2.5 dark:border-white/[0.06] dark:bg-white/[0.03]">
        <p className="truncate text-xs font-medium text-gray-500 dark:text-zinc-400">
          {category.name}
        </p>
        {hasCost && (
          <p className="text-base font-semibold tabular-nums text-gray-900 dark:text-white">
            {costStr}
          </p>
        )}
        {hasConsumption && (
          <p
            className={`tabular-nums ${showBoth ? 'text-xs text-gray-500 dark:text-zinc-400' : 'text-base font-semibold text-gray-900 dark:text-white'}`}
          >
            {consumptionStr}
          </p>
        )}
      </div>
    );
  }

  // Single-category large display
  return (
    <div className="flex h-full flex-col items-center justify-center gap-1 px-2 text-center">
      {hasCost && (
        <p className="text-3xl font-bold tabular-nums text-gray-900 dark:text-white">{costStr}</p>
      )}
      {hasConsumption && (
        <p
          className={`tabular-nums ${showBoth ? 'text-sm text-gray-500 dark:text-zinc-400' : 'text-3xl font-bold text-gray-900 dark:text-white'}`}
        >
          {consumptionStr}
        </p>
      )}
    </div>
  );
}

/**
 * Renderer for Utilities Stat widget
 */
const UtilitiesStatRenderer = memo(function UtilitiesStatRenderer({
  config,
}: RendererProps<UtilitiesStatConfig>) {
  const dashboardTimeRange = useDashboardTimeRange();

  const effectivePreset: string =
    config.timeRangeMode === 'dashboard'
      ? dashboardRangeToPreset(dashboardTimeRange)
      : config.preset;

  const { data, isLoading, isError } = useQuery(
    getGetApiUtilitiesOverviewQueryOptions(
      { preset: effectivePreset },
      { query: { refetchInterval: 60000 } }
    )
  );

  const currencyCode = data?.currencyCode ?? 'EUR';

  const selectedCategories = useMemo(() => {
    const all = data?.categories ?? [];
    return config.categoryIds
      .map((id) => all.find((c) => c.categoryId === id))
      .filter((c): c is UtilitiesCategoryOverview => c !== undefined);
  }, [data?.categories, config.categoryIds]);

  if (config.categoryIds.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-gray-500 dark:text-zinc-500">No categories configured</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-center">
        <AlertTriangle className="mb-2 h-5 w-5 text-amber-500" />
        <p className="text-xs text-gray-500 dark:text-zinc-500">Failed to load utility data</p>
      </div>
    );
  }

  if (selectedCategories.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-center">
        <AlertTriangle className="mb-2 h-5 w-5 text-amber-500" />
        <p className="text-xs text-gray-500 dark:text-zinc-500">
          Selected categories not found — check utilities configuration
        </p>
      </div>
    );
  }

  // Single category: large display
  if (selectedCategories.length === 1) {
    const category = selectedCategories[0]!;
    return (
      <div className="flex h-full flex-col">
        <p className="truncate pb-1 px-1 text-xs text-gray-500 dark:text-zinc-500">
          {category.name}
        </p>
        <CategoryStatCard
          category={category}
          valueMode={config.valueMode}
          currencyCode={currencyCode}
          compact={false}
        />
      </div>
    );
  }

  // Multiple categories: grid of compact cards — columns adapt to count
  const colCount =
    selectedCategories.length <= 3
      ? selectedCategories.length
      : selectedCategories.length === 4
        ? 2
        : 3;
  const gridColsClass =
    colCount === 2 ? 'grid-cols-2' : colCount === 3 ? 'grid-cols-3' : 'grid-cols-4';

  return (
    <div className="flex h-full flex-col gap-1 overflow-y-auto">
      <div className={`grid ${gridColsClass} gap-1.5`}>
        {selectedCategories.map((category) => (
          <CategoryStatCard
            key={category.categoryId}
            category={category}
            valueMode={config.valueMode}
            currencyCode={currencyCode}
            compact={true}
          />
        ))}
      </div>
    </div>
  );
});

/**
 * Utilities Stat widget definition
 */
export const utilitiesStatWidget: WidgetDefinition<UtilitiesStatConfig> = {
  type: 'utilities-stat',
  displayName: 'Utility Stats',
  description: 'Display cost and consumption totals for utility categories',
  icon: Zap,
  defaultSize: { w: 2, h: 2 },
  minSize: { w: 2, h: 2 },
  maxSize: { w: 6, h: 4 },
  configSchema: utilitiesStatConfigSchema,
  defaultConfig: {
    categoryIds: [],
    valueMode: 'both',
    timeRangeMode: 'fixed',
    preset: 'today',
  },
  ConfigForm: UtilitiesStatConfigForm,
  Renderer: UtilitiesStatRenderer,
  supportsTimeRange: true,
};
